// Models
import {AvailableRecordType, RecordNormalityEnum} from "../../../models"

// Utils
import DateUtils from "../../../../../utils/DateUtils"
import {RecordsPeriod} from "../../../utils/dateRange"
import {formatDateTime} from "../../../../../utils/ModernFormat"

/**
 * Column definitions, comparators and row-level derivations for the Modern
 * records table.
 *
 * Column set matches the approved design one-for-one.
 *
 * NOTE on "Patient Age" and "Heart Rate": the list endpoint now returns both,
 * as `age` and `heartRate` (NOT `hr` — an earlier guess at the name here left
 * the column permanently blank). `age` currently comes back null on every
 * record, so that column still renders em dashes; that is the data, not a bug.
 * Do NOT substitute a lookalike field from a different endpoint for either one
 * — a wrong heart rate is far worse than a blank one.
 *
 */

export type SortKey = "dt" | "res" | "age" | "hr" | "name" | "cc"
export type SortDir = 1 | -1
export type SortState = {key: SortKey | null, dir: SortDir}

/**
 * Shared by the records tabs AND the stat cards — clicking a stat card is a
 * period filter, exactly as in the prototype. "month" has no tab of its own and
 * is reachable only from the third stat card.
 */
export type TabKey = RecordsPeriod

export type ColumnDef = {
    key: SortKey | "act"
    labelKey: string
    sortable: boolean
    alignRight: boolean
}

export const COLS: Array<ColumnDef> = [
    {key: "dt", labelKey: "modern.col_datetime", sortable: true, alignRight: false},
    {key: "res", labelKey: "modern.col_result", sortable: true, alignRight: false},
    {key: "age", labelKey: "modern.col_age", sortable: true, alignRight: false},
    {key: "hr", labelKey: "modern.col_hr", sortable: true, alignRight: false},
    {key: "name", labelKey: "modern.col_name", sortable: true, alignRight: false},
    {key: "cc", labelKey: "modern.col_country", sortable: true, alignRight: false},
    {key: "act", labelKey: "modern.col_action", sortable: false, alignRight: true},
];

/**
 * Both are nullable on the payload, and absent altogether on older responses.
 * Return null (not 0) for anything non-numeric so the cell renders an em dash
 * and sorting does not treat "missing" as "lowest".
 *
 * @param record
 */
export function recordAge(record: AvailableRecordType): number | null {
    const value = record ? record.age : null;

    return typeof value === "number" && isFinite(value) ? value : null
}

/**
 * @param record
 */
export function recordHeartRate(record: AvailableRecordType): number | null {
    const value = record ? record.heartRate : null;

    return typeof value === "number" && isFinite(value) ? value : null
}

/** Out-of-range heart rate colours the glyph, exactly as the design specifies. */
export function heartSeverity(record: AvailableRecordType): "good" | "bad" {
    const hr = recordHeartRate(record);

    return hr !== null && (hr < 50 || hr > 100) ? "bad" : "good"
}

export const DEFAULT_SORT: SortState = {key: null, dir: -1};

/**
 *
 * @param current
 * @param key
 */
export function nextSort(current: SortState, key: SortKey): SortState {
    const first: SortDir = key === "dt" ? -1 : 1;

    if (current.key !== key) {
        return {key: key, dir: first}
    }

    return current.dir === first
      ? {key: key, dir: (first === 1 ? -1 : 1)}
      : DEFAULT_SORT
}

/**
 * Epoch millis of the instant a record is DISPLAYED at.
 *
 * Must go through the same `DateUtils` pipeline as `formatRecordTime` below.
 * Parsing `record.time` directly here (as this did) produced a clock offset
 * from the rendered one by twice the UTC offset, so Today/Week/Month bucketed
 * rows by a different day than the one printed in their own date cell: at UTC+2
 * a record rendered "2026-07-21 01:30" was filed under the 20th and vanished
 * from the "Today" tab. Every record within 2x the UTC offset of midnight was
 * misclassified.
 *
 * Sorting is unaffected by which of the two clocks is used — the shift is a
 * constant — but filtering and the "newest loaded record" reference date are
 * not, so both now agree with the cell.
 *
 * @param value
 */
export function toDisplayTime(value: string | number | Date | null | undefined): number {
    if (!value && value !== 0) {
        return 0
    }

    try {
        const shifted = new Date(DateUtils.getDateISOString(String(value))).getTime();

        if (false === isNaN(shifted)) {
            return shifted
        }
    } catch (e) {
        // getDateISOString calls toISOString, which throws RangeError on an
        // unparseable value — fall through to the raw parse.
    }

    const raw = new Date(String(value)).getTime();

    return isNaN(raw) ? 0 : raw
}

/**
 * "Now" on the same display clock, for comparing against `recordTime`.
 */
export function displayNow(): Date {
    return new Date(toDisplayTime(new Date().toISOString()))
}

/**
 * Epoch millis for sorting/filtering. Unparseable times sort to the bottom of a
 * descending list rather than throwing.
 *
 * @param record
 */
export function recordTime(record: AvailableRecordType): number {
    if (!record || !record.time) {
        return 0
    }

    return toDisplayTime(record.time)
}

/**
 * Display time.
 *
 * Uses the same `DateUtils` pipeline as Classic's AvailableRecordTable so the
 * two skins never disagree about when a record arrived — including the timezone
 * handling, which is quirky but is the production behaviour. Falls back to the
 * Modern formatter when `getDateISOString` throws on a malformed value (it
 * calls `toISOString`, which raises RangeError on an invalid Date).
 *
 * @param record
 */
export function formatRecordTime(record: AvailableRecordType): string {
    if (!record || !record.time) {
        return "—"
    }

    try {
        return DateUtils.toStringFormat(
          "yyyy-MM-dd hh:mm",
          DateUtils.getDateISOString(record.time.toString()),
        )
    } catch (e) {
        return formatDateTime(record.time)
    }
}

export type ResultSeverity = "ok" | "warn"

export type ResultMeta = {
    textKey: string | null
    label: string | null
    title: string | null
    severity: ResultSeverity
}


const AFIB_PATTERN = /^atrial\s+fibrillation/;

/**
 * @param conclusion
 */
export function conclusionSeverity(conclusion: string): ResultSeverity {
    return AFIB_PATTERN.test(conclusion.trim().toLowerCase()) ? "warn" : "ok"
}

/**
 * @param conclusion
 */
export function shortConclusion(conclusion: string): string {
    const trimmed = conclusion.trim();
    const firstLine = trimmed.split(/\r?\n/)[0].trim();

    return firstLine.length < trimmed.length ? `${firstLine}…` : firstLine
}

/**
 * @param record
 */
export function resultMeta(record: AvailableRecordType): ResultMeta {
    const conclusion = record && record.algorithmConclusion
      ? String(record.algorithmConclusion).trim()
      : "";

    if (conclusion) {
        return {
            textKey: null,
            label: shortConclusion(conclusion),
            title: conclusion,
            severity: conclusionSeverity(conclusion),
        }
    }

    const normality = record ? record.normality : null;
    const known = normality === RecordNormalityEnum.NORMAL || normality === RecordNormalityEnum.ABNORMAL;

    return {
        textKey: known ? `stats.available_records_table.${normality}` : null,
        label: null,
        title: null,
        severity: normality === RecordNormalityEnum.NORMAL ? "ok" : "warn",
    }
}

/**
 * Whether the pill has anything to render at all.
 *
 * @param meta
 */
export function hasResult(meta: ResultMeta): boolean {
    return meta.label !== null || meta.textKey !== null
}

/** The folder codes production actually returns — see FolderNameEnum. */
const COUNTRY_KEYS: {[code: string]: string} = {
    IT: "modern.country.IT",
    FR: "modern.country.FR",
};

/**
 * i18n key for the full country name shown as the cell's `title`. No flag
 * emoji — they render inconsistently on the Windows workstations this app is
 * read on.
 *
 * Returns null for an unknown or absent code so the caller can fall back to the
 * raw code rather than printing a missing-key string. This module stays
 * i18n-free by design (see `resultText` / `countryText` on FilterOptions).
 *
 * @param folder
 */
export function countryNameKey(folder: string | null | undefined): string | null {
    if (!folder) {
        return null
    }

    return COUNTRY_KEYS[folder] || null
}


export type FilterOptions = {
    query: string
    sort: SortState
    /** Translated result pill text, injected so this module stays i18n-free. */
    resultText(record: AvailableRecordType): string
    /** Translated country name, injected for the same reason. */
    countryText(record: AvailableRecordType): string
}

/**
 * Filter + sort the ACCUMULATED rows. Never mutates the input array.
 *
 * @param records
 * @param options
 */
export function filterAndSortRecords(
  records: Array<AvailableRecordType>,
  options: FilterOptions,
): Array<AvailableRecordType> {
    const {sort, resultText, countryText} = options;
    const query = (options.query || "").trim().toLowerCase();

    const filtered = records.filter(record => {
        if (query) {
            const haystack = [
                record.fileName || "",
                record.folder || "",
                countryText(record),
                record.recordType || "",
                resultText(record),
            ].join(" ").toLowerCase();

            if (haystack.indexOf(query) === -1) {
                return false
            }
        }

        return true
    });
    if (sort.key === null) {
        return filtered
    }

    const value = (record: AvailableRecordType): number | string => {
        switch (sort.key) {
            case "dt":
                return recordTime(record);
            case "res":
                return resultText(record).toLowerCase();
            case "age": {
                const age = recordAge(record);
                return age === null ? -1 : age;
            }
            case "hr": {
                const hr = recordHeartRate(record);
                return hr === null ? -1 : hr;
            }
            case "name":
                return (record.fileName || "").toLowerCase();
            case "cc":
                return (record.folder || "").toLowerCase();
            default:
                return 0
        }
    };

    return filtered.sort((a, b) => {
        const va = value(a);
        const vb = value(b);
        const raw = va < vb ? -1 : va > vb ? 1 : 0;

        return raw * sort.dir
    })
}
